import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import type { DocsKey } from '../../schemas/docs-storage-schema';

import type { KeyStatus } from './key-status';

/** The keys this browser holds for the store; the selected one is what requests send. */
export function KeyList({
  keys,
  selected,
  status,
  onSelect,
  onRemove,
}: {
  keys: readonly DocsKey[];
  selected: string | null;
  status: (key: DocsKey) => KeyStatus;
  onSelect: (prefix: string) => void;
  onRemove: (prefix: string) => void;
}) {
  const { t } = useTranslation('docs');
  const format = useFormat();

  if (keys.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('keys.empty')}</p>;
  }

  return (
    <RadioGroup
      aria-label={t('keys.list')}
      value={selected ?? ''}
      onValueChange={onSelect}
      className="gap-1.5"
    >
      {keys.map((key) => {
        const state = status(key);
        const id = `docs-key-${key.prefix}`;
        return (
          <div
            key={key.prefix}
            data-status={state}
            className="has-[button[data-state=checked]]:border-primary/50 has-[button[data-state=checked]]:bg-accent/40 flex items-center gap-2.5 rounded-md border px-2.5 py-2"
          >
            <RadioGroupItem id={id} value={key.prefix} />
            <Label htmlFor={id} className="grid min-w-0 flex-1 gap-0.5 font-normal">
              <span className="truncate text-sm font-medium">{key.name}</span>
              <span className="text-muted-foreground truncate font-mono text-xs">
                {key.prefix}… · {t('keys.added', { when: format.relative(key.createdAt) })}
              </span>
            </Label>
            {state === 'revoked' || state === 'missing' ? (
              <Badge variant="destructive">{t(`keys.status.${state}`)}</Badge>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={t('keys.remove', { name: key.name, prefix: key.prefix })}
              onClick={() => onRemove(key.prefix)}
            >
              <Trash2 aria-hidden />
            </Button>
          </div>
        );
      })}
    </RadioGroup>
  );
}
